import React, { useState } from 'react';
import { motion } from 'motion/react';
import { useDirectorySearch } from '../../hooks/useDirectorySearch';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  Divider,
  List,
  ListItem,
  Checkbox,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import {
  Group as GroupIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { filterInput } from '../../utils/inputSanitization';

const containerVariants = {
  initial: {},
  animate: {
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

const itemVariants = {
  initial: { opacity: 0, y: 8 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
  },
};

const GroupForm = ({ users = [], onSubmit, onCancel }) => {
  const [groupForm, setGroupForm] = useState({
    name: '',
    users: [],
  });
  const [searchQuery, setSearchQuery] = useState('');
  // Searching hits Keycloak so members can be picked from the whole realm, not
  // just the page the parent happened to preload.
  const userSearch = useDirectorySearch('users', searchQuery);
  const filteredUsers = searchQuery.trim() ? userSearch.results : users;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await onSubmit(groupForm);
    if (success) {
      setGroupForm({
        name: '',
        users: [],
      });
      setSearchQuery('');
    }
  };

  const handleUserToggle = (user, checked) => {
    if (checked) {
      setGroupForm({
        ...groupForm,
        users: [...groupForm.users, user.username],
      });
    } else {
      setGroupForm({
        ...groupForm,
        users: groupForm.users.filter((username) => username !== user.username),
      });
    }
  };

  const isUserSelected = (username) => {
    return groupForm.users.includes(username);
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#FFFFFF',
      borderRadius: '6px',
      fontFamily: 'Metropolis, sans-serif',
      '& fieldset': {
        borderColor: '#E5E5E5',
      },
      '&:hover fieldset': {
        borderColor: '#D64794',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#D64794',
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: 'Metropolis, sans-serif',
      color: '#666666',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#D64794',
    },
  };

  return (
    <motion.div variants={containerVariants} initial="initial" animate="animate">
      {/* Header */}
      <motion.div variants={itemVariants}>
      <Box
        sx={{
          p: 3,
          borderBottom: '1px solid #E5E5E5',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              backgroundColor: '#D64794',
              width: 48,
              height: 48,
            }}
          >
            <GroupIcon sx={{ fontSize: 24 }} />
          </Avatar>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Bitter, Georgia, serif',
                fontWeight: 600,
                fontSize: '1.5rem',
                color: '#253E66',
              }}
            >
              Create Group
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#999999',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 500,
              }}
            >
              Add a new group to the system
            </Typography>
          </Box>
        </Box>
      </Box>
      </motion.div>

      {/* Form Content */}
      <Box sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <motion.div variants={itemVariants}>
          <TextField
            fullWidth
            label="Group Name"
            value={groupForm.name}
            onChange={(e) => setGroupForm({ ...groupForm, name: filterInput(e.target.value, 'GENERAL_TEXT') })}
            required
            size="small"
            sx={{ ...inputStyles, mb: 3 }}
          />
          </motion.div>

          <Divider sx={{ my: 3, borderColor: '#E5E5E5' }} />

          {/* Add Members Section */}
          <motion.div variants={itemVariants}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <PersonAddIcon sx={{ color: '#D64794', fontSize: 20 }} />
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  color: '#253E66',
                }}
              >
                Add Members
              </Typography>
            </Box>

            {/* Search input */}
            <TextField
              fullWidth
              placeholder="Search users..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ ...inputStyles, mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999999' }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* User selection list — stays mounted while a search is active so a
                query that matched nothing cannot strand the admin without a box. */}
            {(users.length > 0 || searchQuery.trim()) ? (
              <Box
                sx={{
                  maxHeight: '240px',
                  overflowY: 'auto',
                  backgroundColor: '#F9FBFD',
                  borderRadius: '6px',
                  border: '1px solid #E5E5E5',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#F9FBFD',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#D9DBDC',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#BDBDBD',
                  },
                }}
              >
                <List sx={{ p: 0 }} dense>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user, index) => (
                      <ListItem
                        key={user.id || user.username}
                        sx={{
                          py: 1,
                          px: 2,
                          borderBottom:
                            index < filteredUsers.length - 1
                              ? '1px solid #E5E5E5'
                              : 'none',
                          '&:hover': {
                            backgroundColor: '#FFFFFF',
                          },
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isUserSelected(user.username)}
                              onChange={(e) => handleUserToggle(user, e.target.checked)}
                              sx={{
                                color: '#BDBDBD',
                                '&.Mui-checked': {
                                  color: '#D64794',
                                },
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar
                                sx={{
                                  width: 28,
                                  height: 28,
                                  backgroundColor: isUserSelected(user.username)
                                    ? '#D64794'
                                    : '#E5E5E5',
                                  color: isUserSelected(user.username)
                                    ? '#FFFFFF'
                                    : '#666666',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  fontFamily: 'Metropolis, sans-serif',
                                }}
                              >
                                {user.username
                                  ? user.username.charAt(0).toUpperCase()
                                  : '?'}
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#253E66',
                                    fontWeight: 500,
                                    fontFamily: 'Metropolis, sans-serif',
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {user.username}
                                </Typography>
                                {user.email && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#999999',
                                      fontFamily: 'Metropolis, sans-serif',
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {user.email}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          }
                          sx={{
                            mx: 0,
                            width: '100%',
                            '& .MuiFormControlLabel-label': {
                              width: '100%',
                            },
                          }}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#999999',
                          fontStyle: 'italic',
                          textAlign: 'center',
                          width: '100%',
                          py: 2,
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 500,
                        }}
                      >
                        No users match your search
                      </Typography>
                    </ListItem>
                  )}
                </List>
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: '#666666',
                  fontStyle: 'italic',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 500,
                }}
              >
                No users available to add to the group
              </Typography>
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                mt: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#666666',
                  fontFamily: 'Metropolis, sans-serif',
                }}
              >
                {groupForm.users.length} user{groupForm.users.length !== 1 ? 's' : ''}{' '}
                selected
              </Typography>
            </Box>
          </Box>
          </motion.div>

          <Divider sx={{ my: 3, borderColor: '#E5E5E5' }} />

          <motion.div variants={itemVariants}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{
                color: '#666666',
                borderColor: '#E5E5E5',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                borderRadius: '6px',
                '&:hover': {
                  borderColor: '#BDBDBD',
                  backgroundColor: '#F9FBFD',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              sx={{
                backgroundColor: '#D64794',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: '#B83A7D',
                },
              }}
            >
              Create Group
            </Button>
          </Box>
          </motion.div>
        </Box>
      </Box>
    </motion.div>
  );
};

export default GroupForm;
